// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skyhold — the board, as real DOM buttons in a CSS grid.
//
// THE RULE THIS FILE IS BUILT AROUND: no layout geometry is ever measured in JavaScript. Cell
// positions come from `grid-area`, cell size from a CSS min() chain, tiers are percentage insets,
// and a token sits in normal flow inside its own cell. That is what makes a backgrounded peer's
// board correct — a tab with clientWidth 0 has nothing to get wrong — and it means every tap is a
// button's own click handler rather than a rect hit-test computed inside a frame that may not have
// run yet.
//
// The particle canvas is the one exception and it is deliberately fixed-size (never height:100%,
// which collapses to the backing-store aspect ratio inside a flex parent) with everything authored
// in 0..1 space.

import {
  DOME,
  buildersOf,
  cellName,
  seatOfBuilder,
  type State,
} from './rules';
import { SEAT_NAME } from './palette';

export interface LastMove {
  from: number;
  to: number;
  build: number;
  seat: number;
}

export interface ViewHints {
  /** Builder index (within the acting seat) currently picked up. */
  selected: number | null;
  moveTargets: Set<number>;
  buildTargets: Set<number>;
  /** Where the selected builder is provisionally standing, mid-turn. */
  pendingAt: number | null;
  pendingFrom: number | null;
  last: LastMove | null;
  /** Cells to flag as sealed-in at the end of a smother. */
  trapped: Set<number>;
  winCell: number | null;
  interactive: boolean;
  mySeat: number | null;
}

export const emptyHints = (): ViewHints => ({
  selected: null,
  moveTargets: new Set(),
  buildTargets: new Set(),
  pendingAt: null,
  pendingFrom: null,
  last: null,
  trapped: new Set(),
  winCell: null,
  interactive: false,
  mySeat: null,
});

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  hue: string;
}

export class BoardView {
  readonly root: HTMLElement;
  private cells: HTMLButtonElement[] = [];
  private fx: HTMLCanvasElement | null = null;
  private parts: Particle[] = [];
  private raf = 0;
  private lastPos = new Map<number, number>();
  private w = 5;
  private h = 5;
  private dead = false;

  constructor(
    host: HTMLElement,
    s: State,
    private readonly onTap: (cell: number) => void,
    private readonly reducedMotion: boolean,
  ) {
    this.w = s.w;
    this.h = s.h;
    this.root = document.createElement('div');
    this.root.className = 'board';
    this.root.style.setProperty('--n', String(s.w));
    this.root.setAttribute('role', 'grid');
    this.root.setAttribute('aria-label', `${s.w} by ${s.h} board`);

    for (let i = 0; i < s.cells; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cell';
      b.dataset.i = String(i);
      b.innerHTML =
        '<i class="tier t1"></i><i class="tier t2"></i><i class="tier t3"></i>' +
        '<i class="dome"></i><b class="lvl"></b><i class="mark"></i>';
      b.addEventListener('click', () => this.onTap(i));
      this.cells.push(b);
      this.root.appendChild(b);
    }
    host.appendChild(this.root);

    if (!reducedMotion) {
      this.fx = document.createElement('canvas');
      this.fx.className = 'fx';
      this.fx.width = 1040;
      this.fx.height = 1040;
      host.appendChild(this.fx);
      this.tick();
    }
  }

  /** Paint the whole board. 25–36 cells, once a second — a diff would be more code than work. */
  paint(s: State, v: ViewHints): void {
    if (this.dead) return;
    const occupiedBy = new Map<number, number>();
    for (let b = 0; b < s.pos.length; b++) {
      const at = s.pos[b];
      if (at >= 0) occupiedBy.set(at, b);
    }
    // A provisional move is drawn where the player put it, not where the state says.
    if (v.pendingAt !== null && v.pendingFrom !== null) {
      const b = occupiedBy.get(v.pendingFrom);
      if (b !== undefined) {
        occupiedBy.delete(v.pendingFrom);
        occupiedBy.set(v.pendingAt, b);
      }
    }

    const dimming = v.moveTargets.size > 0 || v.buildTargets.size > 0;

    for (let i = 0; i < this.cells.length; i++) {
      const el = this.cells[i];
      const lv = s.lev[i];
      el.style.setProperty('--lv', String(lv));
      el.classList.toggle('domed', lv >= DOME);
      const lvl = el.querySelector<HTMLElement>('.lvl')!;
      lvl.textContent = lv > 0 && lv < DOME ? String(lv) : '';

      const isMove = v.moveTargets.has(i);
      const isBuild = v.buildTargets.has(i);
      el.classList.toggle('t-move', isMove);
      el.classList.toggle('t-build', isBuild);
      el.classList.toggle('t-win', v.winCell === i);
      el.classList.toggle('t-trap', v.trapped.has(i));
      el.classList.toggle('ghost', v.pendingFrom === i && v.pendingAt !== null);
      el.classList.toggle('last-from', v.last?.from === i);
      el.classList.toggle('last-to', v.last?.to === i);
      el.classList.toggle('last-build', v.last?.build === i);

      const occ = occupiedBy.get(i);
      el.classList.toggle(
        'dim',
        dimming && !isMove && !isBuild && occ === undefined && lv < DOME && v.last?.to !== i && v.last?.from !== i,
      );

      let tok = el.querySelector<HTMLElement>('.tok');
      if (occ === undefined) {
        tok?.remove();
      } else {
        const seat = seatOfBuilder(s, occ);
        const label = String.fromCharCode(65 + (occ % s.mode.builders));
        if (!tok) {
          tok = document.createElement('i');
          tok.className = 'tok';
          el.appendChild(tok);
        }
        tok.className = `tok p${seat + 1}`;
        tok.textContent = label;
        const picked =
          v.selected !== null && v.mySeat !== null && buildersOf(s, s.turn)[v.selected] === occ;
        tok.classList.toggle('picked', picked);
        this.hop(tok, occ, i);
      }

      const owner = occ === undefined ? '' : `, ${SEAT_NAME[seatOfBuilder(s, occ)]} builder ${String.fromCharCode(65 + (occ % s.mode.builders))}`;
      const height = lv >= DOME ? 'domed' : lv === 0 ? 'ground' : `level ${lv}`;
      el.setAttribute('aria-label', `${cellName(s, i)}, ${height}${owner}`);
      el.disabled = !v.interactive || (!isMove && !isBuild && occ === undefined && !dimming ? false : false);
    }
  }

  /** Slide a token from where it was last painted to where it is now. */
  private hop(tok: HTMLElement, builder: number, cell: number): void {
    const prev = this.lastPos.get(builder);
    this.lastPos.set(builder, cell);
    if (this.reducedMotion || prev === undefined || prev === cell) return;
    const dx = (cell % this.w) - (prev % this.w);
    const dy = ((cell / this.w) | 0) - ((prev / this.w) | 0);
    tok.style.setProperty('--gx', String(-dx));
    tok.style.setProperty('--gy', String(-dy));
    tok.classList.remove('hop');
    void tok.offsetWidth;
    tok.classList.add('hop');
  }

  /** Burst at a cell, in normalised board space so nothing is ever measured. */
  burst(cell: number, colour: string, n = 14): void {
    if (!this.fx || this.reducedMotion) return;
    const x = ((cell % this.w) + 0.5) / this.w;
    const y = (((cell / this.w) | 0) + 0.5) / this.h;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + Math.random();
      const sp = 0.004 + Math.random() * 0.01;
      this.parts.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 0.004, life: 1, hue: colour });
    }
  }

  shake(px = 3): void {
    if (this.reducedMotion) {
      this.root.classList.remove('flash');
      void this.root.offsetWidth;
      this.root.classList.add('flash');
      return;
    }
    this.root.style.setProperty('--shake', `${px}px`);
    this.root.classList.remove('shaking');
    void this.root.offsetWidth;
    this.root.classList.add('shaking');
  }

  nudge(cell: number): void {
    const el = this.cells[cell];
    if (!el) return;
    el.classList.remove('nope');
    void el.offsetWidth;
    el.classList.add('nope');
  }

  private tick = (): void => {
    if (this.dead || !this.fx) return;
    const ctx = this.fx.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, 1040, 1040);
      for (const p of this.parts) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.00045;
        p.life -= 0.022;
      }
      this.parts = this.parts.filter((p) => p.life > 0);
      for (const p of this.parts) {
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.fillStyle = p.hue;
        const r = 6 + p.life * 8;
        ctx.beginPath();
        ctx.arc(p.x * 1040, p.y * 1040, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }
    this.raf = requestAnimationFrame(this.tick);
  };

  destroy(): void {
    this.dead = true;
    cancelAnimationFrame(this.raf);
    this.root.remove();
    this.fx?.remove();
  }
}

/**
 * The isometric board portrait — the "procedural isometric towers" of the original idea, drawn
 * where hit-testing is irrelevant: the results screen. Pure canvas, no interaction, so none of the
 * reasons isometric is wrong for a phone control surface apply.
 */
export function renderIso(canvas: HTMLCanvasElement, s: State, winCell: number | null): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const W = canvas.width;
  const H = canvas.height;
  ctx.clearRect(0, 0, W, H);
  const tw = Math.min(W / (s.w + s.h), 34);
  const th = tw / 2;
  const lift = th * 0.62;
  const ox = W / 2;
  const oy = H / 2 - ((s.w + s.h) * th) / 2 + tw * 0.9;

  const project = (x: number, y: number, z: number): [number, number] => [ox + (x - y) * tw, oy + (x + y) * th - z * lift];

  const face = ['#CDD5E1', '#DCE2EB', '#E8ECF3', '#F4F6FA'];
  for (let y = 0; y < s.h; y++) {
    for (let x = 0; x < s.w; x++) {
      const i = y * s.w + x;
      const lv = s.lev[i];
      const tiers = Math.min(lv, 3);
      for (let z = 0; z < Math.max(1, tiers); z++) {
        const top = project(x, y, z + 1);
        const l = project(x, y, z);
        ctx.beginPath();
        ctx.moveTo(top[0], top[1] - th);
        ctx.lineTo(top[0] + tw, top[1]);
        ctx.lineTo(top[0], top[1] + th);
        ctx.lineTo(top[0] - tw, top[1]);
        ctx.closePath();
        ctx.fillStyle = i === winCell && z + 1 === tiers ? '#F7CF74' : face[Math.min(z, 3)];
        ctx.fill();
        ctx.strokeStyle = 'rgba(16,24,44,.35)';
        ctx.stroke();
        // left and right walls
        ctx.beginPath();
        ctx.moveTo(top[0] - tw, top[1]);
        ctx.lineTo(top[0], top[1] + th);
        ctx.lineTo(l[0], l[1] + th);
        ctx.lineTo(l[0] - tw, l[1]);
        ctx.closePath();
        ctx.fillStyle = '#9AA6BC';
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(top[0] + tw, top[1]);
        ctx.lineTo(top[0], top[1] + th);
        ctx.lineTo(l[0], l[1] + th);
        ctx.lineTo(l[0] + tw, l[1]);
        ctx.closePath();
        ctx.fillStyle = '#7C889E';
        ctx.fill();
      }
      if (lv >= DOME) {
        const t = project(x, y, 3);
        ctx.beginPath();
        ctx.ellipse(t[0], t[1] - th * 0.2, tw * 0.55, th * 0.9, 0, 0, Math.PI * 2);
        ctx.fillStyle = '#1B2740';
        ctx.fill();
      }
      const who = s.occ[i];
      if (who >= 0) {
        const seat = seatOfBuilder(s, who);
        const t = project(x, y, Math.min(lv, 3));
        ctx.fillStyle = seat === 0 ? '#B23B00' : '#0E3E9B';
        ctx.beginPath();
        if (seat === 0) {
          ctx.arc(t[0], t[1] - th * 0.9, tw * 0.42, 0, Math.PI * 2);
        } else {
          ctx.moveTo(t[0], t[1] - th * 2.1);
          ctx.lineTo(t[0] + tw * 0.46, t[1] - th * 0.1);
          ctx.lineTo(t[0] - tw * 0.46, t[1] - th * 0.1);
          ctx.closePath();
        }
        ctx.fill();
      }
    }
  }
}
